<template>
  <div
    v-if="show || force"
    class="w-full shadow rounded-lg p-2 flex items-center gap-2 min-h-[54px] overflow-hidden pointer-events-auto group"
    :class="[NOTIFICATIONS_STYLES[notification.type].wrapper]"
  >
    <!-- START -->
    <FontAwesomeIcon
      class="!flex icon-animation"
      :class="[ICON_CLASS.base, ICON_CLASS[notification.type]]"
      :icon="['far', NOTIFICATIONS_STYLES[notification.type].icon]"
    />
    <!-- BODY -->
    <div class="flex flex-col justify-center flex-grow">
      <div v-if="notification.textBold" class="text-sm text-left font-bold">
        {{ $t(notification.textBold) }}
      </div>
      <div class="text-sm text-left">{{ $t(notification.text) }}</div>
      <div>
        <a
          v-if="notification.link"
          class="mt-1 text-sm underline"
          :href="$t(notification.link.url)"
          target="_blank"
          @click="handleClick"
          >{{ $t(notification.link.text) }}</a
        >
      </div>
    </div>
    <!-- END -->
    <FontAwesomeIcon
      v-if="notification.closeOnClick"
      role="button"
      class="!flex opacity-0 group-hover:opacity-100 transition-opacity"
      :class="[ICON_CLASS, NOTIFICATIONS_STYLES[notification.type].button]"
      :icon="['fas', 'xmark']"
    />
  </div>
</template>

<script setup lang="ts">
import useNotification from '../../composables/useNotification';
import { useAdminStore } from '@getmayday/vue-modules';

const configStore = useConfigStore();
const { isDeprecated, showVideo } = storeToRefs(configStore);
const adminStore = useAdminStore();
const { loginCount } = storeToRefs(adminStore);
const { infoNotification } = useNotification();

const ICON_CLASS = {
  base: 'flex-none rounded items-center justify-center h-5.5 w-5.5 text-lg',
  info: 'text-blue',
  danger: 'text-yellow',
  error: 'text-red',
  success: 'text-green',
};

const NOTIFICATIONS_STYLES = {
  info: {
    icon: 'circle-info',
    wrapper: 'text-blue bg-blue-10',
    button: 'hover:bg-blue hover:text-white transition-colors',
  },
  error: {
    icon: 'exclamation-triangle',
    wrapper: 'text-red bg-red-10',
    button: 'hover:bg-red hover:text-white transition-colors',
  },
  danger: {
    icon: 'exclamation-triangle',
    wrapper: 'text-black bg-yellow-1 border-2 border-yellow',
    button: 'hover:bg-yellow hover:text-white transition-colors',
  },
  success: {
    icon: 'check',
    wrapper: 'text-green bg-green-10',
    button: 'hover:bg-green hover:text-white transition-colors',
  },
};

const props = defineProps<{
  type: 'intro' | 'deprecated';
  force?: boolean;
}>();

const notification = computed(() => {
  return infoNotification(props.type);
});

const show = computed(() => {
  return (
    (isDeprecated.value && props.type === 'deprecated') ||
    (props.type === 'intro' && !showVideo.value && loginCount.value <= 1)
  );
});

const handleClick = () => {
  configStore.hasShowIntroVideo();
};
</script>

<style scoped>
.wrapper-animation {
  width: 54px;
  height: 54px;
  animation-name: expand;
  animation-duration: 250ms;
  animation-fill-mode: forwards;
  animation-delay: 750ms;
  transition-timing-function: ease-in-out;
}

@keyframes expand {
  0% {
    width: 54px;
    height: 54px;
  }
  100% {
    width: 100%;
    height: auto;
  }
}

.text-animation {
  opacity: 0;
  animation-name: fade;
  animation-duration: 250ms;
  animation-fill-mode: forwards;
  animation-delay: 750ms;
  transition-timing-function: ease-in-out;
}

@keyframes fade {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}

.icon-animation {
  transform: rotateZ(0deg);
  animation-name: rotate;
  animation-duration: 500ms;
  animation-fill-mode: forwards;
  animation-delay: 1000ms;
  transform-origin: center center;
  transition-timing-function: ease-in-out;
}

@keyframes rotate {
  0% {
    transform: rotateZ(0deg);
  }
  50% {
    transform: rotateZ(-30deg);
  }
  100% {
    transform: rotateZ(0deg);
  }
}
</style>
